'use client';

import { ShoppingCart } from '@/icons/ShoppingCart';
import { User } from '@/icons/User';
import { getBasket } from '@/lib/BasketStorage';
import { getFavoritesCount } from '@/lib/FavoritesStorage';
import { getIsAnonymous, getLocalUser } from '@/lib/LocalStorage';
import { WebLink } from '@/lib/WebLinks';
import { WindowDispatchEvent } from '@/lib/WindowDispatchEvent';
import { Bell, Heart } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { CampaignLink } from '@/components/CampaignLink';
import OrderTrackViewEntry from './OrderTrackViewEntry';

const PROFILE_ACCOUNT_HREF = `${WebLink.PROFILE}?type=kisisel-bilgilerim`;
const PROFILE_NOTIFICATIONS_HREF = `${WebLink.PROFILE}?type=bildirimlerim`;
const PROFILE_FAVORITES_HREF = `${WebLink.PROFILE}?type=favorilerim`;

const HeaderActions = () => {
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [basketBadgeCount, setBasketBadgeCount] = useState(0);
  const [notificationBadgeCount, setNotificationBadgeCount] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);

  useEffect(() => {
    const anonymousStateListener = () => {
      const anonymousState = getIsAnonymous();
      setIsAnonymous(anonymousState);
      if (anonymousState) {
        setNotificationBadgeCount(0);
        return;
      }
      const localUser = getLocalUser();
      setNotificationBadgeCount(Number(localUser?.badgeCount || 0));
    };

    const basketListener = () => {
      const basket = getBasket();
      const count = basket?.items?.reduce((sum, item) => sum + Number(item.count || 0), 0) || 0;
      setBasketBadgeCount(count);
    };

    window.addEventListener(WindowDispatchEvent.UPDATE_BASKET, basketListener);
    window.addEventListener(WindowDispatchEvent.UPDATE_ANONYMOUS_STATE, anonymousStateListener);
    window.addEventListener(WindowDispatchEvent.UPDATE_USER, anonymousStateListener);

    const favoritesListener = () => {
      setFavoritesCount(getFavoritesCount());
    };

    window.addEventListener(WindowDispatchEvent.UPDATE_FAVORITES, favoritesListener);

    // initial sync
    basketListener();
    anonymousStateListener();
    favoritesListener();

    return () => {
      window.removeEventListener(WindowDispatchEvent.UPDATE_BASKET, basketListener);
      window.removeEventListener(
        WindowDispatchEvent.UPDATE_ANONYMOUS_STATE,
        anonymousStateListener,
      );
      window.removeEventListener(WindowDispatchEvent.UPDATE_USER, anonymousStateListener);
      window.removeEventListener(WindowDispatchEvent.UPDATE_FAVORITES, favoritesListener);
    };
  }, []);

  return (
    <div className="flex">
      {/* Large Screen */}
      <div className="hidden lg:flex space-x-4 justify-center my-auto">
        <OrderTrackViewEntry />

        {!isAnonymous && (
          <CampaignLink
            href={PROFILE_FAVORITES_HREF}
            aria-label="Favorilerim"
            className="relative h-12 w-14 rounded-full hover:cursor-pointer flex justify-center bg-[#5E6AFF1A] hover:bg-hzc_purple_400 items-center transition-all hover:scale-105 duration-300"
          >
            {favoritesCount > 0 && (
              <div className="absolute w-5 h-5 rounded-full bg-hzc_green top-0 end-0 text-hzc_beige_100 text-[12px] text-center">
                {favoritesCount}
              </div>
            )}
            <Heart className="text-white text-xl" />
          </CampaignLink>
        )}

        {isAnonymous ? (
          <CampaignLink
            href={WebLink.LOGIN}
            aria-label="Giriş Yap"
            className="rounded-full h-12 bg-hzc_green flex justify-center items-center hover:cursor-pointer transition-transform hover:scale-105 hover:bg-hzc_green_dark duration-300"
          >
            <span className="py-2 px-4 text-sm text-hzc_purple_500 font-medium">Giriş Yap</span>
          </CampaignLink>
        ) : (
          <>
            <CampaignLink
              href={PROFILE_ACCOUNT_HREF}
              aria-label="Profilim"
              className="h-12 w-14 rounded-full hover:cursor-pointer flex justify-center items-center bg-[#5E6AFF1A] hover:bg-hzc_purple_400 transition-transform hover:scale-105 duration-300"
            >
              <User className="text-white text-lg" />
            </CampaignLink>

            <CampaignLink
              href={PROFILE_NOTIFICATIONS_HREF}
              aria-label="Bildirimler"
              className="relative h-12 w-14 rounded-full hover:cursor-pointer flex justify-center bg-[#5E6AFF1A] hover:bg-hzc_purple_400 items-center transition-all hover:scale-105 duration-300"
            >
              {notificationBadgeCount > 0 && (
                <div className="absolute w-5 h-5 rounded-full bg-hzc_green top-0 end-0 text-hzc_beige_100 text-[12px] text-center">
                  {notificationBadgeCount}
                </div>
              )}
              <Bell className="text-white text-xl" />
            </CampaignLink>
          </>
        )}

        <CampaignLink
          href={WebLink.CART}
          aria-label="Sepetim"
          className="relative h-12 w-14 rounded-full hover:cursor-pointer flex justify-center bg-[#5E6AFF1A] hover:bg-hzc_purple_400 items-center transition-all hover:scale-105 duration-300"
        >
          {basketBadgeCount > 0 && (
            <div className="absolute w-5 h-5 rounded-full bg-hzc_green top-0 end-0 text-hzc_beige_100 text-[12px] text-center">
              {basketBadgeCount}
            </div>
          )}
          <ShoppingCart className="text-white text-xl" />
        </CampaignLink>
      </div>

      {/* Mid Screen */}
      <div className="hidden lg:hidden sm:flex space-x-4 justify-center my-auto">
        <OrderTrackViewEntry />

        {!isAnonymous && (
          <CampaignLink
            href={PROFILE_FAVORITES_HREF}
            aria-label="Favorilerim"
            className="relative h-12 w-14 rounded-full hover:cursor-pointer flex justify-center bg-[#5E6AFF1A] hover:bg-hzc_purple_400 items-center transition-all hover:scale-105 duration-300"
          >
            {favoritesCount > 0 && (
              <div className="absolute w-4 h-4 rounded-full bg-hzc_green -top-0.5 -end-0.5 text-hzc_beige_100 text-[10px] text-center">
                {favoritesCount}
              </div>
            )}
            <Heart className="text-white text-xl" />
          </CampaignLink>
        )}

        <CampaignLink
          href={isAnonymous ? WebLink.LOGIN : PROFILE_ACCOUNT_HREF}
          aria-label="Profilim"
          className="h-12 w-14 rounded-full hover:cursor-pointer flex justify-center items-center bg-[#5E6AFF1A] hover:bg-hzc_purple_400 transition-all hover:scale-105 duration-300"
        >
          <User className="text-white text-lg" />
        </CampaignLink>

        {!isAnonymous && (
          <CampaignLink
            href={PROFILE_NOTIFICATIONS_HREF}
            aria-label="Bildirimler"
            className="relative h-12 w-14 rounded-full hover:cursor-pointer flex justify-center bg-[#5E6AFF1A] hover:bg-hzc_purple_400 items-center transition-all hover:scale-105 duration-300"
          >
            {notificationBadgeCount > 0 && (
              <div className="absolute w-4 h-4 rounded-full bg-hzc_green -top-0.5 -end-0.5 text-hzc_beige_100 text-[10px] text-center">
                {notificationBadgeCount}
              </div>
            )}
            <Bell className="text-white text-xl" />
          </CampaignLink>
        )}

        <CampaignLink
          href={WebLink.CART}
          aria-label="Sepetim"
          className="relative h-12 w-14 rounded-full hover:cursor-pointer flex justify-center bg-[#5E6AFF1A] hover:bg-hzc_purple_400 items-center transition-all hover:scale-105 duration-300"
        >
          {basketBadgeCount > 0 && (
            <div className="absolute w-4 h-4 rounded-full bg-hzc_green -top-0.5 -end-0.5 text-hzc_beige_100 text-[10px] text-center">
              {basketBadgeCount}
            </div>
          )}
          <ShoppingCart className="text-white text-xl" />
        </CampaignLink>
      </div>

      {/* Mobile Screen */}
      <div className="sm:hidden space-x-4 flex justify-center my-auto">
        <OrderTrackViewEntry />

        {!isAnonymous && (
          <CampaignLink
            href={PROFILE_FAVORITES_HREF}
            aria-label="Favorilerim"
            className="relative h-8 w-[42px] rounded-full hover:cursor-pointer flex justify-center items-center bg-[#5E6AFF1A] hover:bg-hzc_purple_400 transition-all hover:scale-105 duration-300 max-[429px]:hidden"
          >
            {favoritesCount > 0 && (
              <div className="absolute w-4 h-4 rounded-full bg-hzc_green -top-0.5 -end-0.5 text-hzc_beige_100 text-[10px] text-center">
                {favoritesCount}
              </div>
            )}
            <Heart className="text-white text-lg" />
          </CampaignLink>
        )}

        <CampaignLink
          href={isAnonymous ? WebLink.LOGIN : PROFILE_ACCOUNT_HREF}
          aria-label="Profilim"
          className="relative h-8 w-[42px] rounded-full hover:cursor-pointer flex justify-center items-center bg-[#5E6AFF1A] hover:bg-hzc_purple_400 transition-all hover:scale-105  duration-300"
        >
          {!isAnonymous && notificationBadgeCount > 0 && (
            <div className="absolute w-4 h-4 rounded-full bg-hzc_green -top-0.5 -end-0.5 text-hzc_beige_100 text-[10px] text-center">
              {notificationBadgeCount}
            </div>
          )}
          <User className="text-white text-lg" />
        </CampaignLink>

        <CampaignLink
          href={WebLink.CART}
          aria-label="Sepetim"
          className="relative h-8 w-[42px] rounded-full hover:cursor-pointer bg-[#5E6AFF1A] hover:bg-hzc_purple_400 flex justify-center items-center transition-all hover:scale-105  duration-300"
        >
          {basketBadgeCount > 0 && (
            <div className="absolute w-4 h-4 rounded-full bg-hzc_green -top-0.5 -end-0.5 text-hzc_beige_100 text-[10px] text-center">
              {basketBadgeCount}
            </div>
          )}
          <ShoppingCart className="text-white text-lg" />
        </CampaignLink>
      </div>
    </div>
  );
};

export default HeaderActions;
